import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { Box, Card, CardActionArea, CardContent, Chip, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import Markdown from 'markdown-to-jsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { captureGoal } from '../analytics';
import { audioSource, imageSource, storyAnchorLabel, storyModelLabel, storySubtitle, type PublishedStory } from '../api/stories';
import { getPersonPath, getStoryPath, getStoryPersonPath, matchPersonPath, useViewRoute } from '../viewRoute';
import { usePublishedStories, usePublishedStory } from '../hooks/useStories';

// The tree's chronicles and portraits, as published from the atelier: a list
// at /:tree/recits, and a page per story at /:tree/recits/:id.

/** Where the stories machine points its links: this site, under any of its names. */
const SITE_HOSTS = new Set(['genealhistory.eu', 'www.genealhistory.eu', 'marchand-viveret.eu', 'www.marchand-viveret.eu']);

/** The path inside this application a story's link leads to, or null when
 * it leads elsewhere (a source). */
function internalPath(href: string): string | null {
    try {
        const url = new URL(href, window.location.origin);
        if (url.origin === window.location.origin || SITE_HOSTS.has(url.hostname)) {
            return `${url.pathname}${url.search}${url.hash}`;
        }
    } catch {
        // Not a URL: left as written.
    }
    return null;
}

function StoryLink({ href, children }: { href?: string; children?: ReactNode }) {
    const { treeName, storyId } = useParams<{ treeName: string; storyId: string }>();
    const path = href ? internalPath(href) : null;
    if (path) {
        // A person or an act opens its drawer over the story, not a page of
        // its own: the reader stays where they are in the text.
        const url = new URL(path, window.location.origin);
        const person = matchPersonPath(url.pathname);
        const to = person && treeName && storyId && person.treeName === treeName
            ? `${getStoryPersonPath(storyId, person.personId, treeName)}${url.search}`
            : path;
        return <Link component={RouterLink} to={to}>{children}</Link>;
    }
    return <Link href={href} target="_blank" rel="noopener noreferrer">{children}</Link>;
}

const MARKDOWN_OPTIONS = { overrides: { a: { component: StoryLink } } };

function kindLabel(story: PublishedStory): string {
    return { chronicle: 'Chronique', portrait: 'Portrait', migration: 'Migration' }[story.kind];
}

function publishedOn(story: PublishedStory): string {
    return story.published_at ? format(new Date(story.published_at), 'd MMMM yyyy', { locale: fr }) : '';
}

export function StoriesIndex() {
    const { treeName } = useParams<{ treeName: string }>();
    const stories = usePublishedStories(treeName);
    return (
        <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>Récits</Typography>
            <Stack
                spacing={1.5}
                sx={{
                    mb: 3.5,
                    pl: 2,
                    borderLeft: '2px solid rgba(217, 119, 6, 0.35)',
                    color: 'text.secondary',
                }}
            >
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    Au-delà de quelques générations, une histoire familiale cesse d’être un fil unique : les lignées se démultiplient, s’entrecroisent et se dispersent de village en village. Il nous en vient un héritage diffus — des lieux, des métiers, des départs, des silences — dont nous héritons sans le savoir, porté par cette foule de femmes et d’hommes grâce à qui nous sommes vivants et que l’on peine pourtant à se représenter.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    Reconstituer, à partir des actes et des archives, quelques fragments de ce qu’ils ont vécu, c’est jeter un coup d’œil dans le passé et tenter de démêler, un à un, les faisceaux d’histoires qui convergent jusqu’à nous. Chroniques de lieux, portraits et récits de migration replacent ainsi chaque pan de cette mémoire dans sa géographie, dans son époque et dans l’arbre familial.
                </Typography>
            </Stack>
            {stories.isLoading && <CircularProgress size={24} />}
            {stories.data?.length === 0 && (
                <Typography color="text.secondary">Aucun récit publié pour le moment.</Typography>
            )}
            <Stack spacing={2}>
                {stories.data?.map((story) => {
                    const anchorName = storyAnchorLabel(story.anchor);
                    return (
                        <Card
                            key={story.id}
                            variant="outlined"
                            sx={{
                                position: 'relative',
                                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                                '&:hover': {
                                    borderColor: 'rgba(217, 119, 6, 0.5)',
                                    bgcolor: 'rgba(217, 119, 6, 0.03)',
                                },
                            }}
                        >
                            <CardContent>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
                                    {story.image && (
                                        <Box
                                            component="img"
                                            src={imageSource(story.image)}
                                            alt=""
                                            loading="lazy"
                                            sx={{
                                                width: { xs: '100%', sm: 220 },
                                                aspectRatio: '4 / 3',
                                                objectFit: 'cover',
                                                borderRadius: 1.5,
                                                border: '1px solid rgba(217, 119, 6, 0.22)',
                                                bgcolor: 'rgba(217, 119, 6, 0.04)',
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                                            <Chip size="small" label={kindLabel(story)} />
                                            {story.has_audio && (
                                                <Chip size="small" variant="outlined" icon={<HeadphonesRoundedIcon />} label="à écouter" />
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {storySubtitle(story)}
                                                {anchorName && (
                                                    <>
                                                        {' · Relié à '}
                                                        {story.anchor_id && treeName ? (
                                                            <Link
                                                                component={RouterLink}
                                                                to={getPersonPath(story.anchor_id, treeName)}
                                                                underline="hover"
                                                                sx={{ position: 'relative', zIndex: 1, fontWeight: 500 }}
                                                            >
                                                                {anchorName}
                                                            </Link>
                                                        ) : (
                                                            anchorName
                                                        )}
                                                    </>
                                                )}
                                                {' · '}{publishedOn(story)}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="h6">
                                            <Link
                                                component={RouterLink}
                                                to={`/${treeName}/recits/${story.id}`}
                                                underline="none"
                                                color="inherit"
                                                onClick={() => captureGoal('story_card_click', { from: 'list', story_id: story.id, kind: story.kind })}
                                                sx={{ '&::after': { content: '""', position: 'absolute', inset: 0 } }}
                                            >
                                                {story.title}
                                            </Link>
                                        </Typography>
                                        {story.excerpt && (
                                            <Typography variant="body2" color="text.secondary">{story.excerpt}</Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
}

/** Where a story happens: a map made once on the server, with France in a
 * corner. Hidden when there is none (nowhere to show, or no map service). */
function StoryMap({ src }: { src: string }) {
    const [failed, setFailed] = useState(false);
    if (failed) return null;
    const image = (
        <Box
            component="img"
            src={src}
            alt="Carte des lieux du récit"
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{
                width: '100%',
                aspectRatio: '16 / 10',
                objectFit: 'cover',
                display: 'block',
                borderRadius: 2,
                border: '1px solid rgba(124, 45, 18, 0.18)',
            }}
        />
    );
    return (
        <Box component="figure" sx={{ m: 0, mt: 2.5 }}>
            {image}
            <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Où se passe ce récit. En encadré, la région sur la carte de France.
            </Typography>
        </Box>
    );
}

/** The story read aloud, at the top of its page, where it is seen first. */
function Listen({ audio, storyId }: { audio: NonNullable<PublishedStory['audio']>; storyId: string }) {
    const minutes = audio.seconds ? Math.max(1, Math.round(audio.seconds / 60)) : null;
    // The first address, kept: each load of the story signs a new one, and a
    // new src would restart the player mid-listening. It stays good for hours.
    const [src] = useState(() => audioSource(audio.url));
    return (
        <Card variant="outlined" sx={{ mt: 2.5, borderColor: 'primary.main', bgcolor: 'rgba(217, 119, 6, 0.06)' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                    <HeadphonesRoundedIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Écouter ce récit{minutes ? ` · ${minutes} min` : ''}
                    </Typography>
                </Stack>
                <Box component="audio" controls preload="none" src={src}
                    onPlay={(event: SyntheticEvent<HTMLAudioElement>) => captureGoal('audio_play', { story_id: storyId, at_seconds: Math.round(event.currentTarget.currentTime) })}
                    sx={{ width: '100%', display: 'block' }}>
                    Votre navigateur ne lit pas l’audio.
                </Box>
            </CardContent>
        </Card>
    );
}

/** Beside an open drawer, the story keeps its place while there is room
 * for it, and moves right only as far as it must not to be covered. */
const BESIDE_DRAWER = {
    ml: 'max(calc((100% - 760px) / 2), calc(var(--event-drawer-width, 0px) + 24px))',
    mr: 'auto',
};

export function StoryPage() {
    const { treeName, storyId, sideItem } = useParams<{ treeName: string; storyId: string; sideItem?: string }>();
    const story = usePublishedStory(treeName, storyId);
    if (story.isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
    if (!story.data) {
        return (
            <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
                <Typography>Ce récit n’existe pas, ou n’est pas publié.</Typography>
                <Link component={RouterLink} to={`/${treeName}/recits`}>Tous les récits</Link>
            </Box>
        );
    }
    const { title, body, sources } = story.data;
    const anchorName = storyAnchorLabel(story.data.anchor);
    return (
        <Box component="article" sx={{ maxWidth: 760, mx: 'auto', py: 2, ...(sideItem ? { '@media (min-width: 600px)': BESIDE_DRAWER } : {}) }}>
            <Link component={RouterLink} to={`/${treeName}/recits`} variant="body2">← Tous les récits</Link>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                sx={{ mt: 2, alignItems: { sm: 'flex-start' } }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="overline" component="p" color="text.secondary">
                        {kindLabel(story.data)} · {storySubtitle(story.data)}
                    </Typography>
                    <Typography variant="h4" component="h1" gutterBottom>{title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Publié le {publishedOn(story.data)}
                        {anchorName && (
                            <>
                                {' · Racine généalogique : '}
                                {story.data.anchor_id && treeName ? (
                                    <Link
                                        component={RouterLink}
                                        to={getStoryPersonPath(story.data.id, story.data.anchor_id, treeName)}
                                        underline="hover"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        {anchorName}
                                    </Link>
                                ) : (
                                    anchorName
                                )}
                            </>
                        )}
                    </Typography>
                    <Typography
                        variant="caption"
                        component="p"
                        color="text.secondary"
                        sx={{
                            mt: 1.5,
                            pl: 1.5,
                            borderLeft: '2px solid',
                            borderColor: 'divider',
                            lineHeight: 1.55,
                            maxWidth: 680,
                        }}
                    >
                        Ce récit a été rédigé avec {storyModelLabel(story.data.model)} à partir des événements de l’arbre et des actes transcrits, avec pour consigne de mettre en regard ces archives familiales et des sources historiques sur les lieux et l’époque, sans rien inventer. Chaque fait renvoie à l’acte ou à la source dont il est tiré.
                    </Typography>
                </Box>
                {story.data.image && (
                    <Box
                        component="img"
                        src={imageSource(story.data.image)}
                        alt=""
                        sx={{
                            width: { xs: '100%', sm: 320 },
                            aspectRatio: '4 / 3',
                            objectFit: 'cover',
                            borderRadius: 2,
                            border: '1px solid rgba(217, 119, 6, 0.25)',
                            boxShadow: '0 2px 10px rgba(69, 26, 3, 0.07)',
                            bgcolor: 'rgba(217, 119, 6, 0.04)',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Stack>
            {story.data.audio && <Listen key={story.data.id} audio={story.data.audio} storyId={story.data.id} />}
            {story.data.map && <StoryMap src={imageSource(story.data.map)} />}
            <Box sx={{ mt: 3, typography: 'body1', lineHeight: 1.7, '& p': { mt: 0, mb: 2 } }}>
                <Markdown options={MARKDOWN_OPTIONS}>{body ?? ''}</Markdown>
            </Box>
            {sources && sources.length > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" component="h2" gutterBottom>Sources</Typography>
                    <Box
                        component="ol"
                        sx={{
                            listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5,
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                        }}
                    >
                        {sources.map((source, index) => (
                            <SourceCard key={index} number={index + 1} source={source} />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

function siteOf(url: string | undefined): string {
    try {
        return url ? new URL(url).hostname.replace(/^www\./, '') : '';
    } catch {
        return '';
    }
}

/** A source as the text cites it, [S3]: its number, the page's title, its site. */
function SourceCard({ number, source }: { number: number; source: NonNullable<PublishedStory['sources']>[number] }) {
    const site = source.site || siteOf(source.url);
    // A source written before its page was read has only its site for a title.
    const title = source.title || site || source.url || '';
    const content = (
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', pt: '2px', minWidth: 24 }}>
                S{number}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}
                >
                    {title}
                </Typography>
                {site && site !== title && (
                    <Typography variant="caption" component="p" noWrap sx={{ color: 'text.secondary' }}>{site}</Typography>
                )}
            </Box>
        </Stack>
    );
    return (
        <Card component="li" variant="outlined" id={`source-${number}`} title={site && site !== title ? `${title} — ${site}` : title}>
            {source.url
                ? <CardActionArea component="a" href={source.url} target="_blank" rel="noopener noreferrer" sx={{ p: 1.5, height: '100%' }}>{content}</CardActionArea>
                : <Box sx={{ p: 1.5 }}>{content}</Box>}
        </Card>
    );
}

/** In a person's drawer: the stories that concern them, if any. */
export function PersonStories({ treeName, grampsId }: { treeName: string | null | undefined; grampsId: string | null | undefined }) {
    const { storyId: activeStoryId } = useViewRoute();
    const stories = usePublishedStories(treeName, grampsId);
    if (!treeName || !grampsId || !stories.data?.length) return null;
    return (
        <Box sx={{ px: '10px', pt: 1.5, pb: 1.25 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1, px: 0.25 }}>
                <MenuBookRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2 }}
                >
                    {stories.data.length > 1 ? 'Récits' : 'Récit'}
                </Typography>
            </Stack>
            <Stack spacing={1}>
                {stories.data.map((story) => {
                    const isCurrent = story.id === activeStoryId;
                    const subtitle = storySubtitle(story);
                    return (
                        <Card
                            key={story.id}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                borderColor: isCurrent ? 'rgba(217, 119, 6, 0.5)' : 'rgba(217, 119, 6, 0.22)',
                                borderLeftWidth: isCurrent ? 3 : 1,
                                borderLeftColor: isCurrent ? 'primary.main' : 'rgba(217, 119, 6, 0.22)',
                                bgcolor: isCurrent ? 'rgba(217, 119, 6, 0.08)' : 'rgba(217, 119, 6, 0.035)',
                                transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': {
                                    borderColor: 'rgba(217, 119, 6, 0.55)',
                                    ...(isCurrent ? { borderLeftColor: 'primary.main' } : {}),
                                    bgcolor: 'rgba(217, 119, 6, 0.085)',
                                    boxShadow: '0 2px 8px rgba(217, 119, 6, 0.10)',
                                    '& .story-card-chevron': {
                                        color: 'primary.main',
                                        transform: 'translateX(2px)',
                                    },
                                },
                            }}
                        >
                            <CardActionArea
                                component={RouterLink}
                                to={getStoryPath(story.id, treeName)}
                                onClick={() => captureGoal('story_card_click', { from: 'drawer', story_id: story.id, kind: story.kind })}
                                aria-current={isCurrent ? 'page' : undefined}
                                sx={{ px: 1.5, py: 1.15 }}
                            >
                                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                                    {story.image && (
                                        <Box
                                            component="img"
                                            src={imageSource(story.image)}
                                            alt=""
                                            loading="lazy"
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 1.25,
                                                objectFit: 'cover',
                                                border: '1px solid rgba(217, 119, 6, 0.25)',
                                                bgcolor: 'rgba(217, 119, 6, 0.04)',
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 0.4, flexWrap: 'wrap' }}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 0.8,
                                                    py: '1px',
                                                    borderRadius: 1,
                                                    fontSize: '0.67rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.04em',
                                                    textTransform: 'uppercase',
                                                    lineHeight: 1.5,
                                                    bgcolor: 'rgba(217, 119, 6, 0.13)',
                                                    color: '#92400e',
                                                }}
                                            >
                                                {kindLabel(story)}
                                            </Box>
                                            {story.has_audio && (
                                                <Stack
                                                    component="span"
                                                    direction="row"
                                                    spacing={0.35}
                                                    sx={{ alignItems: 'center', color: 'primary.main', fontSize: '0.72rem', fontWeight: 500 }}
                                                >
                                                    <HeadphonesRoundedIcon sx={{ fontSize: 13 }} />
                                                    <span>à écouter</span>
                                                </Stack>
                                            )}
                                            {isCurrent && (
                                                <Typography component="span" variant="caption" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                                                    · En lecture
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                lineHeight: 1.35,
                                                color: 'text.primary',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {story.title || subtitle || kindLabel(story)}
                                        </Typography>
                                        {subtitle && story.title && (
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{ display: 'block', color: 'text.secondary', mt: 0.25, lineHeight: 1.3 }}
                                            >
                                                {subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                    <ChevronRightRoundedIcon
                                        className="story-card-chevron"
                                        sx={{
                                            fontSize: 20,
                                            color: 'text.disabled',
                                            flexShrink: 0,
                                            transition: 'transform 0.15s ease, color 0.15s ease',
                                        }}
                                    />
                                </Stack>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
}
